import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router";
import { Check, ChevronDown, LayoutGrid, Pin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildSnapshotCollectionsPath, buildSnapshotGridPath } from "@/modules/snapshots/routes";
import { SnapshotGridButtons } from "@/modules/snapshots/grid/buttons";
import { SnapshotGridActionsMenu } from "@/modules/snapshots/grid/actionsMenu";
import { PermissionAvatarStack } from "@/modules/snapshots/grid/permissionAvatarStack";
import type { SnapshotGridCapabilities } from "@/modules/snapshots/grid/dataSource";
import type { CollectionProps } from "@/modules/snapshots/grid/types";
import { SnapshotViewHeader } from "@/modules/snapshots/snapshotViews/SnapshotViewHeader";
import type { SnapshotView, SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";

interface Props {
  mode: CollectionProps["mode"];
  snapshotView: SnapshotView | undefined;
  businessReport: BusinessReport;
  snapshotCollection: SnapshotCollection;
  siblingCollections: SnapshotCollection[];
  collectionId: string;
  isContractFamilyProjection: boolean;
  subcollectionControl: React.ReactNode;
  subcollectionId: string | undefined;
  subcollectionName: string | undefined;
  gridCapabilities: SnapshotGridCapabilities;
  isFavorited: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
  onSaveView: () => void;
  filteredOutcomeIds: string[] | undefined;
  isGridResultStale: boolean;
  viewConfiguration: SnapshotViewConfiguration;
  hidden: boolean;
}

/** Title row: pin, report / collection switcher / sub-collection, plus share, export and actions. */
export const SnapshotGridHeader: React.FC<Props> = ({
  mode,
  snapshotView,
  businessReport,
  snapshotCollection,
  siblingCollections,
  collectionId,
  isContractFamilyProjection,
  subcollectionControl,
  subcollectionId,
  subcollectionName,
  gridCapabilities,
  isFavorited,
  onToggleFavorite,
  onShare,
  onSaveView,
  filteredOutcomeIds,
  isGridResultStale,
  viewConfiguration,
  hidden,
}) => {
  const navigate = useNavigate();

  return (
    <div className={clsx("mb-3 flex items-center gap-3", hidden && "hidden")}>
      {mode === "view" && snapshotView ? (
        <SnapshotViewHeader view={snapshotView}>{subcollectionControl}</SnapshotViewHeader>
      ) : (
        <>
          <Tooltip
            text={isFavorited ? "Unpin from sidebar" : "Pin to sidebar"}
            delayDuration={100}
            className="rounded bg-black px-3 py-1.5 text-sm font-medium text-white"
          >
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onToggleFavorite}>
              <Pin
                className={clsx(
                  "h-5 w-5 transition-colors",
                  isFavorited ? "rotate-45 fill-coral text-coral" : "text-gray-400",
                )}
              />
            </Button>
          </Tooltip>
          <h3 className="flex items-center gap-2 text-2xl font-semibold">
            {businessReport.name}
            <span className="text-gray-300">/</span>
            <span className="flex h-7 w-7 items-center justify-center rounded bg-orange-100 text-coral">
              <LayoutGrid className="h-4 w-4" />
            </span>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger className="flex items-center gap-1 rounded px-1 hover:bg-gray-100">
                {snapshotCollection.name}
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                {siblingCollections.map(collection => (
                  <DropdownMenuItem
                    key={collection.id}
                    className="cursor-pointer justify-between gap-3"
                    onSelect={() =>
                      navigate(
                        buildSnapshotGridPath(collection.business_report_id, collection.id, {
                          contractFamilyProjection: isContractFamilyProjection,
                        }),
                      )
                    }
                  >
                    <span className={clsx(collection.id === collectionId && "font-semibold")}>{collection.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{collection.snapshots}</span>
                      {collection.id === collectionId && <Check className="h-4 w-4 text-gray-900" />}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="text-gray-300">/</span>
            {isContractFamilyProjection && (
              <span className="rounded-full border border-coral/40 bg-orange-50 px-3 py-1 text-sm font-semibold text-gray-800">
                Family Summary
              </span>
            )}
            {isContractFamilyProjection && <span className="text-gray-300">/</span>}
            {subcollectionControl}
          </h3>
        </>
      )}
      <div className="ml-auto flex items-center gap-3">
        {gridCapabilities.sharing && (
          <PermissionAvatarStack permissions={snapshotCollection.permissions} onClick={onShare} />
        )}
        {gridCapabilities.export && (
          <SnapshotGridButtons
            snapshotCollection={snapshotCollection}
            filteredOutcomeIds={filteredOutcomeIds}
            subcollectionId={subcollectionId}
            subcollectionName={subcollectionName}
            disabled={isGridResultStale || !gridCapabilities.export}
            contractFamilyProjection={isContractFamilyProjection}
            snapshotViewId={snapshotView?.id}
            snapshotViewConfiguration={viewConfiguration}
          />
        )}
        {gridCapabilities.actionsMenu && (
          <SnapshotGridActionsMenu
            snapshotCollection={snapshotCollection}
            onShare={onShare}
            normalViewPath={buildSnapshotCollectionsPath(businessReport.id, snapshotCollection.id, {
              subcollectionId,
            })}
            onSaveView={onSaveView}
            snapshotView={snapshotView}
            summaryColumns={businessReport.prompts}
          />
        )}
      </div>
    </div>
  );
};
